import React, { useEffect, useMemo, useState } from "react";
import { Input, Modal, Select, Switch, Table, Tag, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import COLORS from "common/Colors";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding-top: 4px;
  }
  .sub-text {
    color: ${COLORS.TEXT.PRIMARY};
    margin-bottom: 16px;
  }
  .filters {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 12px;
  }
  .selection-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
    background: #eef1fb;
    border-radius: 8px;
    padding: 8px 14px;
    margin-bottom: 12px;
    min-height: 44px;
  }
  .selection-bar .count {
    font-weight: 600;
    flex: 1 1 220px;
  }
  .selection-bar .actions {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .ant-table-thead > tr > th {
    background: #c0cde2 !important;
    font-weight: 600;
    color: #000;
    white-space: nowrap;
  }
  .employee-name {
    font-weight: 500;
  }
  .employee-id {
    color: ${COLORS.TEXT.PRIMARY};
    font-size: 12px;
  }
  .changed-row > td {
    background: #fffbe6 !important;
  }
  .footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
  }
  .footer .pending {
    margin-right: auto;
    color: ${COLORS.TEXT.PRIMARY};
  }
`;


const toArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);

// The two per-employee flags this window manages - same checkboxes as
// Add/Edit Employee.
const FLAGS = [
  { key: "isemployeeselfkpi", label: "Self KPI" },
  { key: "isemployeeselfprogressreview", label: "Self Progress Review" },
];

const APPLY_TO_OPTIONS = [
  { label: "Self KPI", value: "isemployeeselfkpi" },
  { label: "Self Progress Review", value: "isemployeeselfprogressreview" },
  { label: "Both", value: "both" },
];

// People > "Bulk Self KPI": bulk version of the per-employee "Self KPI" and
// "Self Progress Review" checkboxes in Add/Edit Employee. Edits are held
// locally as a draft (userid -> { flag: newValue }) and only the changed
// employees/flags are sent on save.
function BulkSelfKpiModal({ open, onClose, employeeList = [], departmentList = [], onSaved }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [search, setSearch] = useState("");
  const [applyTo, setApplyTo] = useState("isemployeeselfkpi");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft({});
    setSelectedRowKeys([]);
    setSearch("");
    setApplyTo("isemployeeselfkpi");
  }, [open]);

  const departmentNameById = useMemo(
    () => new Map(departmentList.map((d) => [String(d.department_id), d.department_name])),
    [departmentList]
  );
  const nameByUserId = useMemo(
    () => new Map(employeeList.map((e) => [e.userid, e.fullname])),
    [employeeList]
  );

  const activeEmployees = useMemo(
    () => employeeList.filter((e) => String(e?.statusid) === "1"),
    [employeeList]
  );

  const isChanged = (employee, flag) => flag in (draft[employee.userid] || {});
  const currentValue = (employee, flag) =>
    isChanged(employee, flag) ? draft[employee.userid][flag] : Boolean(employee[flag]);

  const rows = useMemo(() => {
    const searchText = search.trim().toLowerCase();
    if (!searchText) return activeEmployees;
    return activeEmployees.filter(
      (employee) =>
        employee.fullname?.toLowerCase().includes(searchText) ||
        String(employee.employeeid || "").toLowerCase().includes(searchText)
    );
  }, [activeEmployees, search]);

  const setValue = (employee, flag, value) => {
    setDraft((prev) => {
      const userDraft = { ...(prev[employee.userid] || {}) };
      // Switching back to the saved value is no longer a pending change.
      if (Boolean(employee[flag]) === value) delete userDraft[flag];
      else userDraft[flag] = value;
      const next = { ...prev };
      if (Object.keys(userDraft).length) next[employee.userid] = userDraft;
      else delete next[employee.userid];
      return next;
    });
  };

  const applyToSelected = (value) => {
    const selected = new Set(selectedRowKeys);
    const flags = applyTo === "both" ? FLAGS.map((f) => f.key) : [applyTo];
    activeEmployees
      .filter((employee) => selected.has(employee.userid))
      .forEach((employee) => flags.forEach((flag) => setValue(employee, flag, value)));
  };

  const changes = Object.entries(draft).map(([userid, flags]) => ({ userid, ...flags }));
  const enabledCount = (flag) => activeEmployees.filter((e) => currentValue(e, flag)).length;

  const handleClose = () => {
    if (!changes.length) {
      onClose();
      return;
    }
    Modal.confirm({
      title: t("Discard unsaved changes?"),
      content: t("You have {{count}} unsaved change(s).", { count: changes.length }),
      icon: null,
      centered: true,
      okText: t("Discard"),
      cancelText: t("Keep editing"),
      onOk: onClose,
    });
  };

  const handleSave = async () => {
    if (!changes.length) return;
    setSaving(true);
    try {
      const { code, data, message: apiMessage } = await CALL_API(
        "performance-kpi-management/selfkpisettings/bulk",
        "patch",
        { users: changes }
      );
      // The handler answers unknown actions with SUCCESS + "Invalid resource"
      // and no data, so a missing update count means nothing was saved.
      if (code !== STATUS_CODE.SUCCESS || typeof data?.updated !== "number") {
        message.error(apiMessage || t("Failed to update Self KPI settings"));
        return;
      }
      if (data?.failed?.length) {
        const failedNames = data.failed.map((id) => nameByUserId.get(id) || id).join(", ");
        message.warning(t("Updated {{updated}} employee(s). Failed: {{names}}", {
          updated: data.updated,
          names: failedNames,
        }));
      } else {
        message.success(
          t("Self KPI settings updated for {{count}} employee(s)", { count: changes.length })
        );
      }
      onSaved?.();
      onClose();
    } catch (error) {
      console.error("Bulk self KPI update error:", error);
      message.error(t("Failed to update Self KPI settings"));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: t("Employee"),
      key: "employee",
      render: (_, employee) => (
        <>
          <div className="employee-name">{employee.fullname}</div>
          <div className="employee-id">{employee.employeeid || "-"}</div>
        </>
      ),
      sorter: (a, b) => (a.fullname || "").localeCompare(b.fullname || ""),
    },
    {
      title: t("Department"),
      key: "department",
      render: (_, employee) =>
        toArray(employee.basedepartmentid)
          .map((id) => departmentNameById.get(String(id)))
          .filter(Boolean)
          .join(", ") || "-",
    },
    {
      title: t("Reporting Manager"),
      key: "reportingmanager",
      render: (_, employee) => nameByUserId.get(employee.reportingmanager) || "-",
    },
    ...FLAGS.map(({ key, label }) => ({
      title: t(label),
      key,
      width: key === "isemployeeselfkpi" ? 140 : 190,
      render: (_, employee) => (
        <div className="d-flex align-items-center gap-2">
          <Switch
            checked={currentValue(employee, key)}
            onChange={(checked) => setValue(employee, key, checked)}
          />
          {isChanged(employee, key) && <Tag color="gold">{t("Changed")}</Tag>}
        </div>
      ),
    })),
  ];

  return (
    <StyledModal
      title={t("Bulk Self KPI Update")}
      open={open}
      onCancel={handleClose}
      width={1000}
      centered
      destroyOnClose
      footer={
        <div className="footer">
          <span className="pending">
            {changes.length
              ? t("{{count}} employee(s) with pending changes", { count: changes.length })
              : t("No changes yet")}
          </span>
          <ButtonOutlined onClick={handleClose}>{t("Cancel")}</ButtonOutlined>
          <ButtonFilled
            type="primary"
            loading={saving}
            disabled={!changes.length}
            onClick={handleSave}
          >
            {t("Save Changes")}
          </ButtonFilled>
        </div>
      }
    >
      <p className="sub-text">
        {t(
          "Self KPI: the employee fills in and submits their own KPI for their reporting manager to review. Self Progress Review: the employee completes their own progress reviews (Q1 / Mid-Year / Q3) instead of the appraiser."
        )}
        <br />
        {t(
          "Currently enabled for {{selfkpi}} (Self KPI) and {{selfreview}} (Self Progress Review) of {{total}} active employee(s).",
          {
            selfkpi: enabledCount("isemployeeselfkpi"),
            selfreview: enabledCount("isemployeeselfprogressreview"),
            total: activeEmployees.length,
          }
        )}
      </p>

      <div className="filters">
        <Input
          placeholder={t("Search name or employee ID")}
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ width: 280, maxWidth: "100%" }}
        />
      </div>

      <div className="selection-bar">
        <span className="count">
          {selectedRowKeys.length
            ? t("{{count}} selected", { count: selectedRowKeys.length })
            : t("Select employees to update them together")}
        </span>
        <div className="actions">
          <Select
            value={applyTo}
            onChange={setApplyTo}
            options={APPLY_TO_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
            style={{ width: 190 }}
            aria-label={t("Apply to")}
          />
          <ButtonOutlined disabled={!selectedRowKeys.length} onClick={() => applyToSelected(true)}>
            {t("Enable for selected")}
          </ButtonOutlined>
          <ButtonOutlined disabled={!selectedRowKeys.length} onClick={() => applyToSelected(false)}>
            {t("Disable for selected")}
          </ButtonOutlined>
        </div>
      </div>

      <Table
        rowKey="userid"
        columns={columns}
        dataSource={rows}
        size="small"
        pagination={{ pageSize: 10, showSizeChanger: false, hideOnSinglePage: true }}
        // Fixed minimum width so narrow screens scroll sideways instead of
        // squeezing headers like "Department" onto two lines.
        scroll={{ x: 860, y: 360 }}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          // Keep selections made under a previous search instead of dropping them.
          preserveSelectedRowKeys: true,
        }}
        rowClassName={(employee) => (draft[employee.userid] ? "changed-row" : "")}
      />
    </StyledModal>
  );
}

export default BulkSelfKpiModal;
